import storage from "./storage";

// Every tier gets every FEATURE - the only thing that differs between plans
// is how much DATA a tier can access (page-view volume, tracked page slots,
// how far back you can look). This mirrors the backend's
// skydive_backend/src/SubscriptionApi/Helper/entitlements.ts (same shape,
// same FREE_TIER_TITLE constant) rather than round-tripping through a new
// RPC call - the frontend already has the full account record (with
// subscriptionData) via Redux by the time anything needs this, so this is a
// pure function over data that's already there.
//
// Replaces what used to be well over a dozen scattered
// `subscriptionData.title === 'Base Jump'` string comparisons (including at
// least one exact duplicate block) spread across clusterService.ts,
// RightMenu.tsx, AnalyticsView.tsx, and Sidebar.tsx, each independently
// re-deriving the same decision.
export interface Entitlements {
  pageViewsLimit: number;
  // Mirrors the backend's own entitlements.ts - the real ceiling to compare
  // usage against (plan limit + any bonus page-view credit balance).
  // `pageViewsLimit` above stays the plan's own number - billing copy
  // ("your plan includes X views") reads that; usage bars/lock logic
  // should read this instead.
  effectivePageViewsLimit: number;
  bonusPageViewCreditsBalance: number;
  pageViewsLimitType: string;
  urlsToTrack: number;
  // Customer-facing name for the DB/schema field `mainViewSlots` - "Tracked
  // Page Slots" is how many of a site's pages get their own box in the
  // visualization before the rest collapse into "All other pages".
  trackedPageSlots: number;
  storageDays: number;
  isFreeTier: boolean;
  isTrialTier: boolean;
  isTopTier: boolean;
  // How many days back this account's data can be queried from - not
  // computed as a Date here (unlike the backend version) since the
  // frontend's only use for this is comparisons/display, not query params;
  // null means unlimited (storageDays === -1, e.g. the trial plan).
  earliestAccessibleDate: Date | null;
  // Mirrors the backend's own entitlements.ts - see its comment for why
  // these three deliberately break the "every tier gets every feature"
  // rule the fields above follow. Used to visually lock (not hide) the
  // Playback nav link and the flow-lines/interaction-pulses toggles for a
  // Base Jump account, per the 2026-07-22 product decision.
  // How long RAW data survives after it leaves the viewing window - the
  // "upgrade and get your full history back" promise. Deliberately longer
  // than storageDays; see runDataRetentionSweep. Surfaced so the UI can state
  // the real number instead of an unbounded "stays safely stored", which
  // promises more than is kept.
  rawDataRetentionDays: number;
  playbackAllowed: boolean;
  // How far BACK playback may reach, in days. -1 means "as far as this tier's
  // retention allows" (every paid tier). Base Jump gets 1.
  //
  // A separate field rather than clamping earliestAccessibleDate, because that
  // one governs ALL data access - segment panels, historical bubbles,
  // everything. Squeezing it to a day to limit playback would quietly gut
  // features that have nothing to do with playback.
  playbackDays: number;
  // The computed floor for playback specifically. Null means unrestricted
  // beyond the tier's normal retention window.
  earliestPlaybackDate: Date | null;
  flowLinesAllowed: boolean;
  interactionPulsesAllowed: boolean;
  // Mirrors the backend's entitlements.ts: the Visitors tab is a paid feature (paid tiers and the trial), locked on Base Jump. The backend enforces it
  // in the RPCs; this only decides whether the tab shows its content or the standard "isn't available on your plan" note.
  visitorsAllowed: boolean;
  // Whether a HIGHER tier would actually give this account a longer
  // retention window. Deliberately NOT the same question as `isTopTier`:
  // High Altitude and Stratosphere both carry storageDays 365 (they differ
  // on page-view volume, not retention), so gating the "data older than X
  // isn't available - upgrade now" note on `!isTopTier` told a High Altitude
  // account to buy an upgrade that would not move that date by a single day.
  // Compare against the real retention ceiling instead, so the prompt only
  // ever appears where there is something to actually gain.
  retentionUpgradeAvailable: boolean;
}

// See the backend's own entitlements.ts for why this is title-based rather
// than price-based (subscriptionData as snapshotted on the account doesn't
// carry `price`).
export const FREE_TIER_TITLE = "Base Jump";
export const TRIAL_PLAN_TITLE = "14 Days Trial";
export const TOP_TIER_TITLE = "Stratosphere";

// The longest retention window any plan offers - mirrors the `storageDays`
// values in the backend's SubscriptionApi/Constant/subscriptions.const.ts
// (Trial 14, Base Jump 30, High Altitude 365, Stratosphere 365). If a plan's
// retention ever changes there, change it here too.
export const MAX_PLAN_STORAGE_DAYS = 365;

// Base Jump's playback window (Phase 4, 2026-08-27). One day: enough to
// experience "a whole day in four minutes", which is the retention mechanic
// the free tier previously lacked entirely, without replacing the paid tiers -
// where the value is comparing ACROSS days.
//
// Mirrors the backend constant of the same name. Both must agree, or the UI
// would offer a date the API then refuses.
export const FREE_TIER_PLAYBACK_DAYS = 1;

// Mirrors account.ts's runDataRetentionSweep - raw data outlives the viewing
// window on purpose, so an upgrade can restore full history. Free and trial
// tiers share the shorter window: a trial has not paid yet and auto-converts
// to Base Jump at expiry anyway.
export const FREE_TIER_RAW_DATA_RETENTION_DAYS = 90;
export const PAID_TIER_RAW_DATA_RETENTION_DAYS = 365 * 2;

/*
 * 🔴 MIRRORS THE BACKEND'S getEffectiveSubscriptionData, WHICH THIS FILE WAS
 * MISSING ENTIRELY (found 2026-09-26).
 *
 * entitlements.ts exists twice, backend and frontend, and CURRENT_STATUS
 * already warns that drift between them would be silent. This WAS the drift:
 * the backend swaps a live entitlementOverride in for subscriptionData, and
 * the frontend read subscriptionData directly. So a customer granted a
 * special offer got the real limits from the server while the UI showed them
 * the limits of the plan they were nominally on.
 *
 * "Active" means it has a snapshot AND either never expires or has not
 * expired - same test as the backend, including the second-line-of-defence
 * expiry check that covers lag before the sweep clears it.
 */
export const getEffectiveSubscriptionData = (accountRecord: any): any => {
  const override = accountRecord?.entitlementOverride;
  const isActive =
    !!override?.subscriptionData &&
    (override.expiresAt === null ||
      override.expiresAt === undefined ||
      new Date(override.expiresAt) > new Date());

  return isActive ? override.subscriptionData : accountRecord?.subscriptionData;
};

export const getEntitlements = (accountRecord: any): Entitlements => {
  const subscriptionData = getEffectiveSubscriptionData(accountRecord) ?? {};

  const storageDays = subscriptionData.storageDays ?? -1;
  const earliestAccessibleDate =
    storageDays === -1
      ? null
      : new Date(Date.now() - storageDays * 24 * 60 * 60 * 1000);

  const isFreeTier = subscriptionData.title === FREE_TIER_TITLE;

  // Playback's own floor (see playbackDays). Takes the LATER of the two
  // windows: a tier can never replay further back than it retains, so a free
  // tier with 30 days of storage still only replays yesterday, and a paid tier
  // is bounded by retention exactly as before.
  // Day boundary, not a rolling window - see the backend's matching comment.
  // A rolling floor sits mid-day, and playback compares against a day's START,
  // so every day would be refused for most of every day.
  const playbackFloorRaw = isFreeTier
    ? new Date(Date.now() - FREE_TIER_PLAYBACK_DAYS * 24 * 60 * 60 * 1000)
    : null;
  const playbackFloor = playbackFloorRaw
    ? new Date(
        playbackFloorRaw.getFullYear(),
        playbackFloorRaw.getMonth(),
        playbackFloorRaw.getDate()
      )
    : null;
  const earliestPlaybackDate =
    playbackFloor && earliestAccessibleDate
      ? new Date(Math.max(playbackFloor.getTime(), earliestAccessibleDate.getTime()))
      : playbackFloor ?? earliestAccessibleDate;

  const bonusPageViewCreditsBalance =
    accountRecord?.bonusPageViewCredits?.balance ?? 0;
  const pageViewsLimit = subscriptionData.pageViewsLimit ?? -1;

  return {
    pageViewsLimit,
    effectivePageViewsLimit:
      pageViewsLimit === -1
        ? -1
        : pageViewsLimit + bonusPageViewCreditsBalance,
    bonusPageViewCreditsBalance,
    pageViewsLimitType: subscriptionData.pageViewsLimitType ?? "Monthly",
    urlsToTrack: subscriptionData.urlsToTrack ?? -1,
    trackedPageSlots: subscriptionData.mainViewSlots ?? 3,
    storageDays,
    isFreeTier,
    isTrialTier: subscriptionData.title === TRIAL_PLAN_TITLE,
    isTopTier: subscriptionData.title === TOP_TIER_TITLE,
    earliestAccessibleDate,
    /*
     * 🔴 CHANGED 2026-08-27 (Phase 4). Playback is no longer paid-only.
     *
     * From Dana's interview: "the free tier is the part that doesn't retain.
     * Live-view-only is the version with no retention mechanic; the retaining
     * feature - a day in four minutes - was paywalled." Base Jump had nothing
     * to bring anyone back tomorrow, which is a strange thing to do to the
     * plan whose entire job is to make people come back.
     *
     * Base Jump now gets ONE DAY of playback. Enough to experience the thing
     * that actually sells the product; not enough to replace a paid tier,
     * where the value is comparing across days.
     */
    rawDataRetentionDays:
      isFreeTier || subscriptionData.title === TRIAL_PLAN_TITLE
        ? FREE_TIER_RAW_DATA_RETENTION_DAYS
        : PAID_TIER_RAW_DATA_RETENTION_DAYS,
    playbackAllowed: true,
    playbackDays: isFreeTier ? FREE_TIER_PLAYBACK_DAYS : -1,
    earliestPlaybackDate,
    // Flow lines stay paid - the one feature-level gate that survives this
    // change, and the deliberate reason Base Jump is still a free tier rather
    // than a free product.
    flowLinesAllowed: !isFreeTier,
    visitorsAllowed: !isFreeTier,
    /*
     * 🔴 FREE ON ALL TIERS 2026-08-27 (Phase 4). Interaction pulses are the
     * clearest, cheapest demonstration that this is not another analytics
     * dashboard - a visitor visibly reading, hesitating, typing. Charging for
     * the thing that proves the premise made the free tier argue against
     * itself.
     */
    interactionPulsesAllowed: true,
    retentionUpgradeAvailable:
      storageDays !== -1 && storageDays < MAX_PLAN_STORAGE_DAYS,
  };
};

// The page-view lockout copy used to hardcode the word "daily" regardless of
// the account's real pageViewsLimitType - misleading since every tier is
// actually Monthly today, showing "you hit your DAILY limit" immediately
// followed by an unlock date ~30 days out. Mirrors the Daily/Monthly period
// mapping already used for the unlock-date math itself (RightMenu.tsx's
// getUnlockDate, the backend's LockService) so the wording can never drift
// from the actual reset period the way the two were previously computed
// independently.
export const getPageViewsPeriodLabel = (pageViewsLimitType: string): string =>
  pageViewsLimitType === "Daily" ? "daily" : "monthly";

// Persists the two entitlement fields that get read back out of localStorage
// elsewhere (rather than from Redux) - was duplicated verbatim across six
// components (CommonModel, InfoModel, InformationModel, AnalyticsView x2,
// Signup, PaymentModal), each independently writing the same two keys right
// after fetching the account/subscription record.
export const persistTierLocalStorage = (subscriptionData: any) => {
  storage.set("urlsToTrack", subscriptionData?.urlsToTrack);
  storage.set("mainViewSlots", subscriptionData?.mainViewSlots);
};
